import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import EditableSection from '../../../components/EditableSection';
import TaxCategorySelect from '../../../components/tax/TaxCategorySelect';
import { useEditableDraft } from '../../../hooks/useEditableDraft';
import {
    useGetVoucherTaxCategoryQuery,
    useSetVoucherTaxCategoryMutation,
} from '../../../api/voucher/voucherApiSlice';
import { VoucherTaxCategoryResult } from '../../../api/voucher/voucherApiTypes';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import { getApiErrorMessage } from '../../../utils/apiError';

/**
 * The VAT category prepaid vouchers are sold under (#3261 PR 6b). Each sale
 * records the category, country and rate in force that day; choosing it also
 * fills the purchases sold while it was unset. Changing it later governs later
 * sales only. Choosing takes the permission products do.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern): the view
 * shows the stored category, "Bearbeiten" opens a draft of it. It used to save
 * on selection — a choice that also back-fills earlier sales (PO, 2026-10-22).
 */
const VoucherTaxCategorySetting = () => {
    const { t } = useTranslation();
    const canChoose = useSelector(makeHasPermissionSelector(Permission.manageProducts()));
    const { data, isLoading, isError } = useGetVoucherTaxCategoryQuery();
    const [setCategory, { isLoading: isSaving }] = useSetVoucherTaxCategoryMutation();
    const draft = useEditableDraft<string>(data?.taxCategoryId ?? '');
    const [result, setResult] = useState<VoucherTaxCategoryResult | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    const handleSave = async () => {
        setSaveError(null);
        setResult(null);
        try {
            setResult(await setCategory(draft.value).unwrap());
            draft.finish();
        } catch (error) {
            setSaveError(getApiErrorMessage(error) ?? t('vouchers_codes.voucher_vat.save_failed'));
        }
    };

    if (isError) {
        return (
            <p className={'text-sm text-foreground/60 mb-3'}>
                {t('vouchers_codes.voucher_vat.load_failed')}
            </p>
        );
    }

    /** The category — locked in the view, editable in the draft. */
    const renderCategory = (locked: boolean) => (
        <div>
            <TaxCategorySelect
                help={t('vouchers_codes.voucher_vat.help')}
                value={draft.value}
                onChange={draft.setDraft}
                disabled={locked || isLoading || isSaving}
            />
            {/* The stored value as this unit knows it: right after Speichern the
                refetch has not landed yet, and "not chosen" must not show beside
                "Gespeichert". */}
            {!isLoading && !draft.base && (
                <p className={'text-sm text-foreground/60 mt-2'}>
                    {t('vouchers_codes.voucher_vat.unset', { count: data?.purchasesWithoutVat ?? 0 })}
                </p>
            )}
            {locked && result && (
                <p className={'text-sm text-foreground/60 mt-2'}>
                    {t('vouchers_codes.voucher_vat.saved', { count: result.backfilled })}
                    {result.unresolved > 0 &&
                        ` ${t('vouchers_codes.voucher_vat.unresolved', { count: result.unresolved })}`}
                </p>
            )}
        </div>
    );

    return (
        <div className={'mb-4 max-w-md'}>
            <EditableSection
                title={t('vouchers_codes.voucher_vat.label')}
                readOnly={!canChoose || isLoading}
                editing={draft.editing}
                isDirty={draft.isDirty}
                saving={isSaving}
                canSave={draft.value !== ''}
                error={saveError}
                onEdit={() => { setResult(null); draft.startEdit(); }}
                onCancel={() => { setSaveError(null); draft.cancel(); }}
                onSave={() => { void handleSave(); }}
                view={renderCategory(true)}
            >
                {renderCategory(false)}
            </EditableSection>
        </div>
    );
};

export default VoucherTaxCategorySetting;
